export type ReportTaxonomyDataType = 'string' | 'boolean' | 'number' | 'date' | 'duration';

export interface ReportTaxonomySort {
	field: string;
	direction: 'ASC' | 'DESC';
}

export interface ReportTaxonomyField {
	key: string;
	label: string;
	dataType: ReportTaxonomyDataType;
	categorical: boolean;
	filterable: boolean;
	sortable: boolean;
	groupable: boolean;
	operators: string[];
	/** The vocabulary a categorical field's value is drawn from, as the wire spells it - `RUNNING`,
	 * `FIXED_PRICE`, `Green`. Absent for a field with no closed value set. What a reader is SHOWN for
	 * one of these is a display decision that does not belong to the contract: the wire value is what
	 * a filter condition has to carry, so the two can legitimately differ.
	 *
	 * Its presence, not the data type, is also what says a value picker can be built from the catalog
	 * alone - see `resolverKindFor`'s `enumeratedValues` kind. Absent on every field whose values are
	 * ids resolved as live entities. Optional because most fields do not carry it. */
	values?: string[];
	/** Names the condition under which this field has no value to show, so a reader can be told the
	 * difference between "no retainer period is running" and "we do not know". Null when the field
	 * always applies. */
	notApplicableRule?: string | null;
	/** True when the field's unit is per-row rather than fixed for the grain (a task estimate is
	 * hours or points depending on its project; a project's logged hours are always hours). */
	unitVaries?: boolean;
	/** True for a company-defined custom field injected into the catalog, which is groupable but
	 * neither filterable nor sortable - the upstream filter vocabulary cannot address one. */
	custom?: boolean;
	/** Null for a field that cannot be expressed as a filter condition at all - 15 fields in the
	 * task catalog carry it, and `fieldByFilterField`/`ConditionsEditor`/`addCondition` all guard on
	 * it. Declaring it non-null made those guards look redundant to the type checker while the
	 * vendored taxonomy really does ship nulls. */
	filterField: string | null;
	context: string;
	unit: string | null;
}

export interface ReportTaxonomy {
	reportType: string;
	version: string;
	fields: ReportTaxonomyField[];
	operators: {value: string; label: string}[];
	valueResolvers: Record<string, string>;
	contexts: {value: string; label: string}[];
	/** The field paths a report of this grain shows when nobody has chosen otherwise, in order. */
	defaultColumns: string[];
	/** The column a report of this grain sorts by when its definition names none. */
	defaultSort: ReportTaxonomySort | null;
	/** The grouping tokens this grain can be grouped by. The two grains do not share a vocabulary -
	 * a task report groups by phase and assignee, a project-status report by stage and RAG - so this
	 * is the only correct source for what the Group By control may offer, and for which token a
	 * stored definition is allowed to carry. */
	groupingValues: string[];
}

/** Parses the taxonomy JSON the graphql proxy returns.
 *
 * Every key is defaulted rather than trusted: the taxonomy is published by report-service and can
 * legitimately be a version ahead or behind this client, and a missing key must degrade the surface
 * that reads it rather than throw on the way in. */
export function parseReportTaxonomy(json: string): ReportTaxonomy {
	const raw = JSON.parse(json);
	return {
		reportType: raw.reportType,
		version: raw.version,
		fields: raw.fields ?? [],
		operators: raw.operators ?? [],
		valueResolvers: raw.valueResolvers ?? {},
		contexts: raw.contexts ?? [],
		defaultColumns: raw.defaultColumns ?? [],
		defaultSort: raw.defaultSort ?? null,
		groupingValues: raw.groupingValues ?? [],
	};
}

export function operatorLabel(taxonomy: ReportTaxonomy, op: string): string {
	return taxonomy.operators.find(o => o.value === op)?.label ?? op;
}

export function fieldByKey(taxonomy: ReportTaxonomy, key: string): ReportTaxonomyField | undefined {
	return taxonomy.fields.find(f => f.key === key);
}

/** Looks up a field by its `filterField` enum (STATUS, PROJECT, LABELS, ...). Filter conditions
 * key on the filterField, NOT the taxonomy `key` (`task.status.name`) that columns/grouping use —
 * the two vocabularies coexist by design, so a condition must resolve its field through here. */
export function fieldByFilterField(
	taxonomy: ReportTaxonomy,
	filterField: string | null | undefined
): ReportTaxonomyField | undefined {
	// Guard against a null/empty lookup key: several taxonomy fields carry `filterField: null`
	// (non-filterable derived fields), so a `=== null` match would silently return the first of
	// those instead of "no match". A condition can only reference a field by a real filterField.
	if (!filterField) {
		return undefined;
	}
	return taxonomy.fields.find(f => f.filterField === filterField);
}

/** Remaining work is filtered through a Yes/No control rather than a number entry, so the builder
 * needs a kind for it. It is deliberately NOT a `valueResolvers` entry in the taxonomy: that map
 * means "resolve these values as live entities", and the authoring agent sends every kind it finds
 * there to entity resolution, where a presentation-only kind would fail. Presentation is the
 * client's business, so the mapping lives here. */
const PRESENTATION_KINDS: Record<string, string> = {REMAINING: 'remainingWork'};

/** Kind for a field whose candidate values the catalog publishes outright. Like the presentation
 * kinds it is deliberately not a `valueResolvers` entry - those mean "resolve these values as live
 * entities" and the authoring agent sends every kind it finds there to entity resolution, which a
 * closed vocabulary of labels would fail. The list IS the resolution, so the client builds the
 * picker straight from it and can never offer a value the engine cannot apply.
 *
 * Being generic, this opens EVERY field that publishes a vocabulary, not a chosen few. On the
 * project-status catalog that is five: Progress, Retainer over/under, Status (RAG), Stage and
 * Budget type. Opening all five is a decision, not an oversight - the two the ticket asked for
 * cannot be singled out without an allowlist, and an allowlist is the hand-maintained copy of the
 * catalog this kind exists to avoid.
 *
 * Stage and Budget type publish raw wire tokens (`RUNNING`, `TIME_AND_MATERIALS`,
 * `FIXED_PRICE_V2`) rather than prose, so those two read as tokens in the picker. That is accepted
 * rather than unnoticed: the table already renders the very same tokens in its Stage and Budget
 * type columns, so the picker agrees with what is on screen. Giving them prose means translated
 * strings for a vocabulary report-service owns, and it should change both surfaces at once or
 * neither. */
export const ENUMERATED_VALUES_KIND = 'enumeratedValues';

export function resolverKindFor(taxonomy: ReportTaxonomy, field: ReportTaxonomyField): string | undefined {
	const filterField = field.filterField;
	return (
		(filterField ? taxonomy.valueResolvers[filterField] : undefined) ??
		taxonomy.valueResolvers[field.key] ??
		(filterField ? PRESENTATION_KINDS[filterField] : undefined) ??
		// Last, not first: an explicit resolver entry and a hand-declared presentation control both
		// describe this field specifically, and either one outranks the generic fallback. An empty
		// list is no vocabulary at all - resolving a kind for one would open a picker with nothing in
		// it, telling the reader the field is filterable and then giving them no way to filter.
		(field.values && field.values.length > 0 ? ENUMERATED_VALUES_KIND : undefined)
	);
}

/** Resolver kinds the client can back with a usable, company-wide value source (the relay-backed
 * pickers plus the workflow-status categories in `useValueSource`'s registry). A categorical field
 * is only value-editable when its resolver kind is one of these. Keep this set in sync with the
 * editable entries of `conditions/valueSources/useValueSource`'s registry.
 *
 * `enumeratedValues` is the one entry with no registry row of its own: its candidates come from the
 * field being asked about rather than from a fixed list, so `useValueSource` builds it per field
 * from `field.values` instead of looking it up.
 *
 * `workflowStatus` belongs here even though status COLUMNS are per-project: the status filter is
 * matched against a task's status category, whose vocabulary is the same three values company-wide,
 * so the picker has a complete, unambiguous value set with no project scoping needed. */
const VALUE_EDITABLE_RESOLVER_KINDS = new Set([
	'project',
	'person',
	'client',
	'label',
	'role',
	'workflowStatus',
	'remainingWork',
	ENUMERATED_VALUES_KIND,
]);

export function isValueEditable(taxonomy: ReportTaxonomy, field: ReportTaxonomyField): boolean {
	// The taxonomy is what says which fields a reader may filter on, and this is the flag it says it
	// with. Read it first: a boolean field answers true below without consulting a resolver at all,
	// so a check placed after that would not cover one. Every non-filterable field in today's catalog
	// also carries `filterField: null`, which keeps it out of the add-condition list by a second
	// route - but the client must honour the declaration itself, not rely on that coincidence.
	if (!field.filterable) {
		return false;
	}
	if (field.dataType === 'boolean') {
		return true;
	}
	const kind = resolverKindFor(taxonomy, field);
	if (kind === undefined || !VALUE_EDITABLE_RESOLVER_KINDS.has(kind)) {
		return false;
	}
	// A non-categorical field is only editable through a control that stands in for the comparison,
	// which is what the presentation kinds are. An id-backed kind on a non-categorical field would
	// be a taxonomy mistake, so it stays excluded.
	return field.categorical || (field.filterField !== null && PRESENTATION_KINDS[field.filterField] === kind);
}
